import { Color, Entity, TranslateGizmo, Vec3, createGraphicsDevice } from 'playcanvas';

import { registerCameraPosesEvents } from './camera-poses';
import { registerDocEvents } from './doc';
import { EditHistory } from './edit-history';
import { registerEditorEvents } from './editor';
import { Events } from './events';
import { initFileHandler } from './file-handler';
import { registerPlySequenceEvents } from './ply-sequence';
import { registerPublishEvents } from './publish';
import { registerRenderEvents } from './render';
import { Scene } from './scene';
import { getSceneConfig } from './scene-config';
import { registerSelectionEvents } from './selection';
import { Shortcuts } from './shortcuts';
import { registerTimelineEvents } from './timeline';
import { BoxSelection } from './tools/box-selection';
import { BrushSelection } from './tools/brush-selection';
import { LassoSelection } from './tools/lasso-selection';
import { MoveTool } from './tools/move-tool';
import { PolygonSelection } from './tools/polygon-selection';
import { RectSelection } from './tools/rect-selection';
import { RotateTool } from './tools/rotate-tool';
import { ScaleTool } from './tools/scale-tool';
import { SphereSelection } from './tools/sphere-selection';
import { MeasureTool } from './tools/measure-tool';
import { ToolManager } from './tools/tool-manager';
import { registerTransformHandlerEvents } from './transform-handler';
import { EditorUI } from './ui/editor';
import { registerImageImportEvents } from './image-doc';
import { registerPoseJsonEvents } from './image-config-doc';
import { registerImageAssociationEvents } from './image-association';
import { registerSplatTransformHandler } from './splat-transform-handler';
import { registerBlgImportEvents } from './blg-doc';
import { registerPdfDownload } from './pdf-download';
import { registerClickableZoneEvents } from './clickable-zone';

declare global {
    interface LaunchParams {
        readonly files: FileSystemFileHandle[];
    }

    interface Window {
        launchQueue: {
            setConsumer: (callback: (launchParams: LaunchParams) => void) => void;
        };
        scene: Scene;
    }
}

const getURLArgs = () => {
    // extract settings from command line in non-prod builds only
    const config = {};

    const apply = (key: string, value: string) => {
        let obj: any = config;
        key.split('.').forEach((k, i, a) => {
            if (i === a.length - 1) {
                obj[k] = value;
            } else {
                if (!obj.hasOwnProperty(k)) {
                    obj[k] = {};
                }
                obj = obj[k];
            }
        });
    };

    const params = new URLSearchParams(window.location.search.slice(1));
    params.forEach((value: string, key: string) => {
        apply(key, value);
    });

    return config;
};

const initShortcuts = (events: Events) => {
    const shortcuts = new Shortcuts(events);

    // shortcuts.register(['Delete', 'Backspace'], { event: 'select.delete' });
    // shortcuts.register(['Escape'], { event: 'tool.deactivate' });
    // shortcuts.register(['Tab'], { event: 'selection.next' });
    // shortcuts.register(['1'], { event: 'tool.move', sticky: true });
    // shortcuts.register(['2'], { event: 'tool.rotate', sticky: true });
    // shortcuts.register(['3'], { event: 'tool.scale', sticky: true });
    shortcuts.register(['G', 'g'], { event: 'grid.toggleVisible' });
    shortcuts.register(['C', 'c'], { event: 'tool.toggleCoordSpace' });
    // shortcuts.register(['F', 'f'], { event: 'camera.focus' });
    // shortcuts.register(['R', 'r'], { event: 'tool.rectSelection', sticky: true });
    // shortcuts.register(['P', 'p'], { event: 'tool.polygonSelection', sticky: true });
    // shortcuts.register(['L', 'l'], { event: 'tool.lassoSelection', sticky: true });
    // shortcuts.register(['B', 'b'], { event: 'tool.brushSelection', sticky: true });
    // shortcuts.register(['A', 'a'], { event: 'select.all', ctrl: true });
    // shortcuts.register(['A', 'a'], { event: 'select.none', shift: true });
    // shortcuts.register(['I', 'i'], { event: 'select.invert', ctrl: true });
    // shortcuts.register(['H', 'h'], { event: 'select.hide' });
    // shortcuts.register(['U', 'u'], { event: 'select.unhide' });
    // shortcuts.register(['['], { event: 'tool.brushSelection.smaller' });
    // shortcuts.register([']'], { event: 'tool.brushSelection.bigger' });
    // shortcuts.register(['Z', 'z'], { event: 'edit.undo', ctrl: true, capture: true });
    // shortcuts.register(['Z', 'z'], { event: 'edit.redo', ctrl: true, shift: true, capture: true });
    shortcuts.register(['M', 'm'], { event: 'camera.toggleMode' });
    // shortcuts.register(['D', 'd'], { event: 'dataPanel.toggle' });
    shortcuts.register([' '], { event: 'camera.toggleOverlay' });

    return shortcuts;
};

const main = async () => {
    // root events object
    const events = new Events();

    // url
    const url = new URL(window.location.href);

    // edit history
    const editHistory = new EditHistory(events);

    // editor ui
    const editorUI = new EditorUI(events);

    // create the graphics device
    const graphicsDevice = await createGraphicsDevice(editorUI.canvas, {
        deviceTypes: ['webgl2'],
        antialias: false,
        depth: false,
        stencil: false,
        xrCompatible: false,
        powerPreference: 'high-performance'
    });

    const overrides = [
        getURLArgs()
    ];

    // resolve scene config
    const sceneConfig = getSceneConfig(overrides);

    // construct the manager
    const scene = new Scene(
        events,
        sceneConfig,
        editorUI.canvas,
        graphicsDevice
    );

    // colors
    const bgClr = new Color();
    const selectedClr = new Color();
    const unselectedClr = new Color();
    const lockedClr = new Color();

    const setClr = (target: Color, value: Color, event: string) => {
        if (!target.equals(value)) {
            target.copy(value);
            events.fire(event, target);
        }
    };

    const setBgClr = (clr: Color) => {
        setClr(bgClr, clr, 'bgClr');
    };
    const setSelectedClr = (clr: Color) => {
        setClr(selectedClr, clr, 'selectedClr');
    };
    const setUnselectedClr = (clr: Color) => {
        setClr(unselectedClr, clr, 'unselectedClr');
    };
    const setLockedClr = (clr: Color) => {
        setClr(lockedClr, clr, 'lockedClr');
    };

    events.on('setBgClr', (clr: Color) => {
        setBgClr(clr);
    });
    events.on('setSelectedClr', (clr: Color) => {
        setSelectedClr(clr);
    });
    events.on('setUnselectedClr', (clr: Color) => {
        setUnselectedClr(clr);
    });
    events.on('setLockedClr', (clr: Color) => {
        setLockedClr(clr);
    });

    events.function('bgClr', () => {
        return bgClr;
    });
    events.function('selectedClr', () => {
        return selectedClr;
    });
    events.function('unselectedClr', () => {
        return unselectedClr;
    });
    events.function('lockedClr', () => {
        return lockedClr;
    });

    events.on('bgClr', (clr: Color) => {
        const cnv = (v: number) => `${Math.max(0, Math.min(255, (v * 255))).toFixed(0)}`;
        document.body.style.backgroundColor = `rgba(${cnv(clr.r)},${cnv(clr.g)},${cnv(clr.b)},1)`;
    });
    events.on('selectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('unselectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('lockedClr', (clr: Color) => {
        scene.forceRender = true;
    });

    // initialize colors from application config
    const toColor = (value: { r: number, g: number, b: number, a: number }) => {
        return new Color(value.r, value.g, value.b, value.a);
    };
    setBgClr(toColor(sceneConfig.bgClr));
    setSelectedClr(toColor(sceneConfig.selectedClr));
    setUnselectedClr(toColor(sceneConfig.unselectedClr));
    setLockedClr(toColor(sceneConfig.lockedClr));

    // create the mask selection canvas
    const maskCanvas = document.createElement('canvas');
    const maskContext = maskCanvas.getContext('2d');
    maskCanvas.setAttribute('id', 'mask-canvas');
    maskContext.globalCompositeOperation = 'copy';

    const mask = {
        canvas: maskCanvas,
        context: maskContext
    };

    // tool manager
    const toolManager = new ToolManager(events);
    toolManager.register('rectSelection', new RectSelection(events, editorUI.toolsContainer.dom));
    toolManager.register('brushSelection', new BrushSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('polygonSelection', new PolygonSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('lassoSelection', new LassoSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('sphereSelection', new SphereSelection(events, scene, editorUI.canvasContainer));
    toolManager.register('boxSelection', new BoxSelection(events, scene, editorUI.canvasContainer));
    toolManager.register('measure', new MeasureTool(events, scene, editorUI.toolsContainer.dom));
    toolManager.register('move', new MoveTool(events, scene));
    toolManager.register('rotate', new RotateTool(events, scene));
    toolManager.register('scale', new ScaleTool(events, scene));

    editorUI.toolsContainer.dom.appendChild(maskCanvas);

    window.scene = scene;

    registerEditorEvents(events, editHistory, scene);
    registerSelectionEvents(events, scene);
    registerTimelineEvents(events);
    registerCameraPosesEvents(events);
    registerTransformHandlerEvents(events);
    registerPlySequenceEvents(events);
    registerPublishEvents(events);
    registerDocEvents(scene, events);
    registerRenderEvents(scene, events);
    initShortcuts(events);
    initFileHandler(scene, events, editorUI.appContainer.dom);
    registerImageImportEvents(events);
    registerPoseJsonEvents(scene, events);
    registerImageAssociationEvents(events);
    registerSplatTransformHandler(events);
    registerBlgImportEvents(events);
    registerPdfDownload(events);
    registerClickableZoneEvents(events, scene, editorUI.canvas);

    // Evidence creation gizmo — lets the user drag the new zone position in 3D
    {
        let gizmo: TranslateGizmo | null = null;
        let pivot: Entity | null = null;

        const updateGizmoSize = () => {
            if (!gizmo) return;
            gizmo.size = scene.camera.ortho
                ? 1125 / scene.canvas.clientHeight
                : 1200 / Math.max(scene.canvas.clientWidth, scene.canvas.clientHeight);
        };
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);

        events.on('evidenceGizmo.attach', (position: Vec3) => {
            if (!gizmo) {
                gizmo = new TranslateGizmo(scene.camera.entity.camera, scene.gizmoLayer);
                gizmo.on('render:update', () => { scene.forceRender = true; });
                gizmo.on('transform:move', () => {
                    if (!pivot) return;
                    const p = pivot.getPosition();
                    events.fire('evidenceGizmo.moved', new Vec3(p.x, p.y, p.z));
                });
                updateGizmoSize();
            }
            if (!pivot) {
                pivot = new Entity('evidence-gizmo-pivot');
                scene.app.root.addChild(pivot);
            }
            pivot.setPosition(position.x, position.y, position.z);
            gizmo.attach([pivot]);
        });

        events.on('evidenceGizmo.setPosition', (position: Vec3) => {
            if (pivot) pivot.setPosition(position.x, position.y, position.z);
        });

        events.on('evidenceGizmo.detach', () => {
            if (gizmo) gizmo.detach();
        });
    }

    events.on('clickableZone.clicked', (id: string) => {
        if (id === 'placeholder-zone') {
            console.log('[ClickableZone] Placeholder clicked!');
        }
    });

    // load async models
    scene.start();

    // handle load params
    const loadList = url.searchParams.getAll('load');
    for (const value of loadList) {
        const decoded = decodeURIComponent(value);
        await events.invoke('import', [{
            filename: decoded.split('/').pop(),
            url: decoded
        }]);
    }

    // Show the sample launcher only when the page was not opened with a scene URL.
    // File-association launches hide it as soon as their BLGS import starts.
    if (loadList.length === 0) {
        events.fire('samples.show');
    }

    // handle OS-based file association in PWA mode
    if ('launchQueue' in window) {
        window.launchQueue.setConsumer(async (launchParams: LaunchParams) => {
            for (const file of launchParams.files) {
                await events.invoke('import', [{
                    filename: file.name,
                    contents: await file.getFile()
                }]);
            }
        });
    }
};

export { main };
